import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { PageLayout } from "@/components/layout/PageLayout";
import { Helmet } from "react-helmet-async";
import { useWallet } from "@/contexts/WalletContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Users, MessageSquare, Trophy, Gift, Link as LinkIcon,
  Copy, Crown, Star, Activity, Send, Plus, Loader2
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "@/hooks/use-toast";
import { formatDistanceToNow } from "date-fns";
import { Link } from "react-router-dom";

export default function CommunityHub() {
  const { connected, publicKey, connect } = useWallet();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("activity");
  const [message, setMessage] = useState("");
  const chatRef = useRef<HTMLDivElement>(null);

  // Fetch communities user is member of
  const { data: myCommunities } = useQuery({
    queryKey: ["my-communities", publicKey],
    queryFn: async () => {
      if (!publicKey) return [];
      const { data } = await supabase
        .from("community_members")
        .select("*, communities(*)")
        .eq("wallet", publicKey);
      return data || [];
    },
    enabled: !!publicKey,
  });

  // Fetch all communities
  const { data: communities } = useQuery({
    queryKey: ["all-communities"],
    queryFn: async () => {
      const { data } = await supabase
        .from("communities")
        .select("*")
        .order("member_count", { ascending: false })
        .limit(20);
      return data || [];
    },
  });

  // Fetch community members
  const { data: members } = useQuery({
    queryKey: ["community-members"],
    queryFn: async () => {
      const { data } = await supabase
        .from("community_members")
        .select("*, profiles(*)")
        .order("contribution_sol", { ascending: false })
        .limit(20);
      return data || [];
    },
  });

  // Fetch chat messages - using a global community chat
  const { data: chatMessages, isLoading: chatLoading } = useQuery({
    queryKey: ["community-chat"],
    queryFn: async () => {
      const { data } = await supabase
        .from("chat_messages")
        .select("*")
        .is("coin_id", null) // Global chat
        .order("timestamp", { ascending: false })
        .limit(50);
      return (data || []).reverse();
    },
    refetchInterval: 3000,
  });

  // Real-time chat subscription
  useEffect(() => {
    const channel = supabase
      .channel("community-chat-realtime")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "chat_messages" },
        () => {
          queryClient.invalidateQueries({ queryKey: ["community-chat"] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  // Auto-scroll chat
  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [chatMessages]);

  // Send message mutation
  const sendMessage = useMutation({
    mutationFn: async () => {
      if (!message.trim() || !publicKey) return;
      
      const { error } = await supabase.from("chat_messages").insert({
        id: crypto.randomUUID(),
        user_name: publicKey.slice(0, 8),
        message: message.trim(),
        coin_id: null, // Global chat
        timestamp: Math.floor(Date.now() / 1000),
      });

      if (error) throw error;
    },
    onSuccess: () => {
      setMessage("");
      queryClient.invalidateQueries({ queryKey: ["community-chat"] });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  // Fetch leaderboard
  const { data: leaderboard } = useQuery({
    queryKey: ["community-leaderboard"],
    queryFn: async () => {
      const { data } = await supabase
        .from("leaderboard_entries")
        .select("*")
        .order("total_profit", { ascending: false })
        .limit(10);
      return data || [];
    },
  });

  // Fetch recent trades for activity
  const { data: recentTrades } = useQuery({
    queryKey: ["community-activity"],
    queryFn: async () => {
      const { data } = await supabase
        .from("trades")
        .select("*, coins(name, ticker)")
        .order("timestamp", { ascending: false })
        .limit(10);
      return data || [];
    },
  });

  const copyReferralLink = () => {
    if (!publicKey) return;
    const link = `${window.location.origin}?ref=${publicKey.slice(0, 8)}`;
    navigator.clipboard.writeText(link);
    toast({ title: "Copied!", description: "Referral link copied to clipboard" });
  };

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (message.trim()) {
      sendMessage.mutate();
    }
  };

  if (!connected) {
    return (
      <>
        <Helmet><title>Community Hub - TheMoonRun</title></Helmet>
        <PageLayout>
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <Users className="w-16 h-16 text-primary/30 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-foreground mb-2">Connect Wallet</h2>
              <p className="text-muted-foreground mb-4">Connect your wallet to join the community</p>
              <Button variant="hero" onClick={connect}>Connect Wallet</Button>
            </div>
          </div>
        </PageLayout>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Community Hub - TheMoonRun</title>
        <meta name="description" content="Join trading communities, compete in challenges, and earn rewards together." />
      </Helmet>

      <PageLayout>
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20 flex items-center justify-center">
              <Users className="w-7 h-7 text-blue-500" />
            </div>
            <div>
              <h1 className="text-2xl font-display font-bold text-foreground">Community Hub</h1>
              <p className="text-muted-foreground">Trade together, earn together</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Badge variant="outline" className="text-green-500 border-green-500/50">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse mr-2" />
              {members?.length || 0} Members
            </Badge>
            <Button variant="hero" asChild>
              <Link to="/communities">
                <Plus className="w-4 h-4" />
                Browse Communities
              </Link>
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="activity">Activity</TabsTrigger>
                <TabsTrigger value="challenges">Challenges</TabsTrigger>
                <TabsTrigger value="leaderboard">Leaderboard</TabsTrigger>
                <TabsTrigger value="giveaways">Giveaways</TabsTrigger>
              </TabsList>

              <TabsContent value="activity" className="space-y-4">
                {/* Chat */}
                <div className="p-4 rounded-xl bg-card border border-border">
                  <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-primary" />
                    Community Chat
                  </h3>
                  
                  <div 
                    ref={chatRef}
                    className="h-64 overflow-y-auto space-y-3 mb-4 p-3 rounded-lg bg-secondary/30"
                  >
                    {chatLoading ? (
                      <div className="flex items-center justify-center h-full">
                        <Loader2 className="w-6 h-6 animate-spin text-primary" />
                      </div>
                    ) : chatMessages && chatMessages.length > 0 ? (
                      chatMessages.map((msg: any) => (
                        <div key={msg.id} className="flex items-start gap-2">
                          <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                            <span className="text-xs font-bold text-primary">
                              {msg.user_name?.charAt(0)?.toUpperCase() || "?"}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-medium text-foreground">
                                {msg.user_name || "Anonymous"}
                              </span>
                              {msg.is_dev && (
                                <Badge variant="default" className="text-[10px] px-1.5">DEV</Badge>
                              )}
                              <span className="text-xs text-muted-foreground">
                                {msg.timestamp 
                                  ? formatDistanceToNow(new Date(msg.timestamp * 1000), { addSuffix: true })
                                  : "just now"}
                              </span>
                            </div>
                            <p className="text-sm text-muted-foreground break-words">{msg.message}</p>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-center text-muted-foreground py-8">No messages yet. Start the conversation!</p>
                    )}
                  </div>

                  <form onSubmit={handleSendMessage} className="flex gap-2">
                    <Input
                      placeholder="Type a message..."
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      disabled={sendMessage.isPending}
                    />
                    <Button 
                      type="submit" 
                      variant="hero" 
                      disabled={!message.trim() || sendMessage.isPending}
                    >
                      {sendMessage.isPending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                    </Button>
                  </form>
                </div>

                {/* Recent Activity */}
                <div className="p-4 rounded-xl bg-card border border-border">
                  <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                    <Activity className="w-5 h-5 text-primary" />
                    Recent Activity
                  </h3>
                  <div className="space-y-3">
                    {recentTrades && recentTrades.length > 0 ? (
                      recentTrades.map((trade: any) => (
                        <div key={trade.id} className="flex items-center justify-between p-3 rounded-lg bg-secondary/30">
                          <span className="text-sm text-foreground">
                            {trade.wallet?.slice(0, 6)}... {trade.type === "buy" ? "bought" : "sold"} ${trade.coins?.ticker || "???"}
                          </span>
                          <span className={cn(
                            "font-mono text-sm font-semibold",
                            trade.type === "sell" ? "text-destructive" : "text-success"
                          )}>
                            {trade.type === "buy" ? "+" : "-"}{trade.amount_sol?.toFixed(2) || 0} SOL
                          </span>
                        </div>
                      ))
                    ) : (
                      <p className="text-center text-muted-foreground py-4">No recent activity</p>
                    )}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="challenges" className="space-y-4">
                <div className="grid gap-4">
                  {[
                    { title: "Weekly Trading Challenge", prize: "10 SOL", participants: 234, endsIn: "3d 12h" },
                    { title: "Best Referrer", prize: "5 SOL", participants: 89, endsIn: "5d 6h" },
                    { title: "Diamond Hands Challenge", prize: "2.5 SOL", participants: 567, endsIn: "1d 8h" },
                  ].map((challenge, i) => (
                    <div key={i} className="p-4 rounded-xl bg-card border border-border">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-semibold text-foreground">{challenge.title}</h3>
                          <p className="text-sm text-muted-foreground">{challenge.participants} participants</p>
                        </div>
                        <Badge variant="outline" className="text-primary border-primary/50">
                          {challenge.endsIn}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Trophy className="w-5 h-5 text-yellow-500" />
                          <span className="font-mono font-bold text-foreground">{challenge.prize}</span>
                        </div>
                        <Button variant="outline" size="sm">Join Challenge</Button>
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="leaderboard" className="space-y-4">
                <div className="p-4 rounded-xl bg-card border border-border">
                  <h3 className="font-semibold text-foreground mb-4">Top Traders</h3>
                  <div className="space-y-2">
                    {leaderboard && leaderboard.length > 0 ? (
                      leaderboard.map((entry: any, i) => (
                        <div key={entry.id} className="flex items-center gap-3 p-3 rounded-lg bg-secondary/30">
                          <span className={cn(
                            "w-8 h-8 rounded-lg flex items-center justify-center font-bold",
                            i === 0 && "bg-yellow-500/20 text-yellow-500",
                            i === 1 && "bg-gray-400/20 text-gray-400",
                            i === 2 && "bg-orange-500/20 text-orange-500",
                            i > 2 && "bg-secondary text-muted-foreground"
                          )}>
                            {i + 1}
                          </span>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-foreground font-mono text-sm">
                                {entry.wallet_address?.slice(0, 6)}...{entry.wallet_address?.slice(-4)}
                              </span>
                              {i === 0 && <Crown className="w-4 h-4 text-yellow-500" />}
                            </div>
                            <span className="text-xs text-muted-foreground">
                              {entry.win_rate ? `${(entry.win_rate * 100).toFixed(0)}% win rate` : "No trades yet"}
                            </span>
                          </div>
                          <span className={cn(
                            "font-mono font-bold",
                            (entry.total_profit || 0) >= 0 ? "text-success" : "text-destructive"
                          )}>
                            {(entry.total_profit || 0) >= 0 ? "+" : ""}{(entry.total_profit || 0).toFixed(2)} SOL
                          </span>
                        </div>
                      ))
                    ) : (
                      <p className="text-center text-muted-foreground py-8">No leaderboard data yet</p>
                    )}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="giveaways" className="space-y-4">
                <div className="text-center py-8">
                  <Gift className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                  <p className="text-muted-foreground">No active giveaways</p>
                  <p className="text-sm text-muted-foreground">Check back soon!</p>
                </div>
              </TabsContent>
            </Tabs>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Referral Card */}
            <div className="p-4 rounded-xl bg-card border border-border">
              <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                <LinkIcon className="w-5 h-5 text-primary" />
                Your Referral Link
              </h3>
              <p className="text-xs text-muted-foreground mb-3">
                Share to earn 0.5% of trading fees from referred users
              </p>
              <div className="flex gap-2">
                <Input 
                  value={publicKey ? `themoon.run/?ref=${publicKey.slice(0, 8)}` : "Connect wallet"}
                  readOnly 
                  className="text-xs font-mono" 
                />
                <Button variant="outline" size="icon" onClick={copyReferralLink}>
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Top Members */}
            <div className="p-4 rounded-xl bg-card border border-border">
              <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                Top Contributors
              </h3>
              <div className="space-y-2">
                {members && members.length > 0 ? (
                  members.slice(0, 5).map((member: any, i) => (
                    <div key={member.wallet} className="flex items-center gap-2 p-2 rounded-lg hover:bg-secondary/30 transition-colors">
                      <span className={cn(
                        "w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold",
                        i === 0 && "bg-yellow-500/20 text-yellow-500",
                        i === 1 && "bg-gray-400/20 text-gray-400",
                        i === 2 && "bg-orange-500/20 text-orange-500",
                        i > 2 && "bg-secondary text-muted-foreground"
                      )}>
                        {i + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate font-mono">
                          {member.wallet?.slice(0, 6)}...{member.wallet?.slice(-4)}
                        </p>
                        <p className="text-xs text-muted-foreground capitalize">{member.role || "member"}</p>
                      </div>
                      <span className="text-xs font-mono text-success">
                        +{(member.contribution_sol || 0).toFixed(1)} SOL
                      </span>
                    </div>
                  ))
                ) : (
                  <p className="text-center text-muted-foreground py-4 text-sm">No members yet</p>
                )}
              </div>
            </div>

            {/* Your Stats */}
            <div className="p-4 rounded-xl bg-card border border-border">
              <h3 className="font-semibold text-foreground mb-3">Your Stats</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Referrals</span>
                  <span className="font-mono text-foreground">0</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Earnings</span>
                  <span className="font-mono text-success">0 SOL</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Rank</span>
                  <span className="font-mono text-foreground">#--</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </PageLayout>
    </>
  );
}
